/**
 * DateTimePicker Component
 *
 * A simple date picker that supports single date or date range selection.
 *
 * @example
 * ```jsx
 * // Single date mode
 * <DateTimePicker
 *   value="2024-04-18"
 *   onChange={(value) => setForm({ ...form, date: value })}
 *   placeholder="Select date"
 * />
 *
 * // Range mode
 * <DateTimePicker
 *   mode="range"
 *   startDate="2024-04-18"
 *   endDate="2024-04-22"
 *   onChange={(startDate, endDate) => {
 *     setForm({ ...form, startDate, endDate });
 *   }}
 *   placeholder="Select date range"
 * />
 * ```
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
	CalendarIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { classNames } from '../helpers/functions';

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

const sizeClasses = {
	sm: 'h-8 px-2 py-1 text-xs',
	md: 'h-10 px-3 py-2 text-sm',
	lg: 'h-12 px-4 py-3 text-base',
};

const iconSizes = {
	sm: 'h-3 w-3',
	md: 'h-4 w-4',
	lg: 'h-5 w-5',
};

/**
 * Parse date string to Date object
 */
const parseDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return value;
	if (typeof value === 'string') {
		// If it's an ISO string with UTC timezone (ends with Z), parse it and use local date
		// This handles dates stored in UTC that need to be displayed in local time
		if (value.includes('T') && value.endsWith('Z')) {
			const date = new Date(value);
			// Return a date with just the local date portion (no time)
			return new Date(date.getFullYear(), date.getMonth(), date.getDate());
		}
		// Otherwise extract the date portion directly (for local date strings)
		const dateOnly = value.split('T')[0];
		const [year, month, day] = dateOnly.split('-').map(Number);
		return new Date(year, month - 1, day);
	}
	return null;
};

/**
 * Format Date to date string (YYYY-MM-DD)
 */
const formatDateString = (date) => {
	if (!date) return '';
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Format Date for display
 */
const formatDisplayDate = (date) => {
	if (!date) return '';
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
	});
};

/**
 * Format date range for display
 */
const formatDisplayRange = (startDate, endDate) => {
	if (!startDate) return '';
	const start = formatDisplayDate(startDate);
	if (!endDate || isSameDay(startDate, endDate)) {
		return start;
	}
	const end = formatDisplayDate(endDate);
	return `${start} - ${end}`;
};

// Get days in month
const getDaysInMonth = (year, month) => {
	return new Date(year, month + 1, 0).getDate();
};

// Get first day of month (0 = Sunday)
const getFirstDayOfMonth = (year, month) => {
	return new Date(year, month, 1).getDay();
};

// Check if two dates are the same day
const isSameDay = (date1, date2) => {
	if (!date1 || !date2) return false;
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};

// Check if date is today
const isToday = (date) => {
	return isSameDay(date, new Date());
};

// Check if date is between start and end (inclusive)
const isBetween = (date, start, end) => {
	if (!date || !start || !end) return false;
	const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const startOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
	const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
	return dateOnly >= startOnly && dateOnly <= endOnly;
};

// Check if date is before minDate
const isBeforeMin = (date, minDate) => {
	if (!minDate) return false;
	const min = parseDate(minDate);
	if (!min) return false;
	return date < new Date(min.getFullYear(), min.getMonth(), min.getDate());
};

// Check if date is after maxDate
const isAfterMax = (date, maxDate) => {
	if (!maxDate) return false;
	const max = parseDate(maxDate);
	if (!max) return false;
	return date > new Date(max.getFullYear(), max.getMonth(), max.getDate());
};

const DateTimePicker = ({
	// Single mode props
	value,
	// Range mode props
	mode = 'single',
	startDate: startDateProp,
	endDate: endDateProp,
	// Common props
	onChange,
	placeholder = 'Select date',
	minDate,
	maxDate,
	disabled = false,
	className,
	size = 'md',
	// Legacy props (ignored for backwards compatibility)
	showTimeToggle,
	defaultAllDay,
	allDay,
}) => {
	const isRangeMode = mode === 'range';

	// Parse dates based on mode
	const singleValue = !isRangeMode ? parseDate(value) : null;
	const rangeStart = isRangeMode ? parseDate(startDateProp) : null;
	const rangeEnd = isRangeMode ? parseDate(endDateProp) : null;

	const [isOpen, setIsOpen] = useState(false);
	const [viewDate, setViewDate] = useState(() => {
		if (isRangeMode && rangeStart) return rangeStart;
		if (singleValue) return singleValue;
		return new Date();
	});

	// Range selection state
	const [selectingEnd, setSelectingEnd] = useState(false);

	const triggerRef = useRef(null);
	const dropdownRef = useRef(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });

	// Update view date when props change
	useEffect(() => {
		if (isRangeMode && rangeStart) {
			setViewDate(rangeStart);
		} else if (singleValue) {
			setViewDate(singleValue);
		}
	}, [value, startDateProp, endDateProp, isRangeMode]);

	// Calculate position when opening
	useEffect(() => {
		if (isOpen && triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			const dropdownHeight = 320;
			const spaceBelow = window.innerHeight - rect.bottom;

			setPosition({
				top:
					spaceBelow > dropdownHeight
						? rect.bottom + 4
						: rect.top - dropdownHeight - 4,
				left: rect.left,
				width: Math.max(rect.width, 288),
			});
		}
	}, [isOpen]);

	// Click outside handler
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e) => {
			if (
				triggerRef.current &&
				!triggerRef.current.contains(e.target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target)
			) {
				setIsOpen(false);
				setSelectingEnd(false);
			}
		};

		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				setIsOpen(false);
				setSelectingEnd(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpen]);

	const handleSelect = (day) => {
		const selectedDate = new Date(
			viewDate.getFullYear(),
			viewDate.getMonth(),
			day
		);

		if (isRangeMode) {
			const dateString = formatDateString(selectedDate);

			if (!rangeStart || !selectingEnd) {
				// First selection or resetting - set both start and end to same date
				// This ensures a valid single-day selection even if user clicks outside
				onChange(dateString, dateString);
				setSelectingEnd(true);
			} else {
				// Second selection - set end (or update range)
				let start = rangeStart;
				let end = selectedDate;

				// Swap if end is before start
				if (end < start) {
					[start, end] = [end, start];
				}

				const startString = formatDateString(start);
				const endString = formatDateString(end);
				onChange(startString, endString);
				setSelectingEnd(false);
				setIsOpen(false);
			}
		} else {
			// Single mode
			const dateString = formatDateString(selectedDate);
			onChange(dateString);
			setIsOpen(false);
		}
	};

	const handleClear = (e) => {
		e.stopPropagation();
		if (isRangeMode) {
			onChange('', '');
		} else {
			onChange('');
		}
		setIsOpen(false);
		setSelectingEnd(false);
	};

	const goToPrevMonth = (e) => {
		e.stopPropagation();
		setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
	};

	const goToNextMonth = (e) => {
		e.stopPropagation();
		setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
	};

	const goToToday = (e) => {
		e.stopPropagation();
		const today = new Date();
		const dateString = formatDateString(today);

		if (isRangeMode) {
			// Set both start and end to today for a valid single-day selection
			onChange(dateString, dateString);
			setSelectingEnd(true);
		} else {
			onChange(dateString);
			setIsOpen(false);
		}

		setViewDate(today);
	};

	const year = viewDate.getFullYear();
	const month = viewDate.getMonth();
	const daysInMonth = getDaysInMonth(year, month);
	const firstDay = getFirstDayOfMonth(year, month);

	// Build calendar grid
	const calendarDays = [];
	for (let i = 0; i < firstDay; i++) {
		calendarDays.push(null);
	}
	for (let day = 1; day <= daysInMonth; day++) {
		calendarDays.push(day);
	}

	// Display value
	const displayValue = isRangeMode
		? formatDisplayRange(rangeStart, rangeEnd)
		: (singleValue ? formatDisplayDate(singleValue) : '');

	const hasValue = isRangeMode ? (rangeStart || rangeEnd) : singleValue;

	const dropdown = isOpen &&
		createPortal(
			<div
				ref={dropdownRef}
				className="fixed z-[9999] w-72 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
				style={{ top: position.top, left: position.left }}
			>
				<div className="p-3">
					{/* Range mode hint */}
					{isRangeMode && (
						<div className="mb-2 text-xs text-gray-500 text-center">
							{!rangeStart
								? 'Select a date'
								: selectingEnd
								? 'Select end date or click outside for single day'
								: 'Click to select new date'}
						</div>
					)}

					{/* Header with month/year navigation */}
					<div className="flex items-center justify-between mb-3">
						<button
							type="button"
							onClick={goToPrevMonth}
							className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
						>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>
						<span className="text-sm font-semibold text-gray-900">
							{MONTHS[month]} {year}
						</span>
						<button
							type="button"
							onClick={goToNextMonth}
							className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
						>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
					</div>

					{/* Day headers */}
					<div className="grid grid-cols-7 gap-1 mb-1">
						{DAYS.map((day) => (
							<div
								key={day}
								className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
							>
								{day}
							</div>
						))}
					</div>

					{/* Calendar grid */}
					<div className="grid grid-cols-7 gap-1">
						{calendarDays.map((day, index) => {
							if (day === null) {
								return <div key={`empty-${index}`} className="h-8" />;
							}

							const cellDate = new Date(year, month, day);
							const isSelected = isRangeMode
								? (isSameDay(cellDate, rangeStart) || isSameDay(cellDate, rangeEnd))
								: isSameDay(cellDate, singleValue);
							const isInRange = isRangeMode && isBetween(cellDate, rangeStart, rangeEnd);
							const isTodayDate = isToday(cellDate);
							const isDisabled =
								isBeforeMin(cellDate, minDate) || isAfterMax(cellDate, maxDate);
							const isRangeStart = isRangeMode && isSameDay(cellDate, rangeStart);
							const isRangeEnd = isRangeMode && isSameDay(cellDate, rangeEnd);

							return (
								<button
									key={day}
									type="button"
									disabled={isDisabled}
									onClick={() => handleSelect(day)}
									className={classNames(
										'h-8 w-full rounded-md text-sm font-medium transition-colors',
										'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
										isSelected
											? 'bg-indigo-600 text-white hover:bg-indigo-700'
											: isInRange
											? 'bg-indigo-100 text-indigo-900 hover:bg-indigo-200'
											: isTodayDate
											? 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
											: 'text-gray-700 hover:bg-gray-100',
										isDisabled &&
											'opacity-40 cursor-not-allowed hover:bg-transparent',
										isRangeStart && 'rounded-r-none',
										isRangeEnd && 'rounded-l-none',
										isInRange && !isRangeStart && !isRangeEnd && 'rounded-none'
									)}
								>
									{day}
								</button>
							);
						})}
					</div>

					{/* Footer */}
					<div className="mt-3 pt-3 border-t border-gray-100 flex justify-between">
						<button
							type="button"
							onClick={goToToday}
							className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
						>
							Today
						</button>
						{hasValue && (
							<button
								type="button"
								onClick={handleClear}
								className="text-xs font-medium text-gray-500 hover:text-gray-700"
							>
								Clear
							</button>
						)}
					</div>
				</div>
			</div>,
			document.body
		);

	return (
		<div className="relative">
			<button
				ref={triggerRef}
				type="button"
				disabled={disabled}
				onClick={() => setIsOpen(!isOpen)}
				className={classNames(
					'flex w-full items-center justify-between rounded-lg border bg-white shadow-sm transition-colors',
					'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
					'disabled:cursor-not-allowed disabled:opacity-50',
					isOpen
						? 'border-indigo-500 ring-2 ring-indigo-500 ring-offset-1'
						: 'border-gray-300',
					sizeClasses[size],
					className
				)}
			>
				<div className="flex items-center gap-2">
					<CalendarIcon
						className={classNames(iconSizes[size], 'text-gray-400')}
					/>
					<span className={hasValue ? 'text-gray-900' : 'text-gray-400'}>
						{displayValue || placeholder}
					</span>
				</div>
				{hasValue && !disabled && (
					<span
						role="button"
						tabIndex={0}
						onClick={handleClear}
						onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
						className="ml-2 p-0.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
					>
						<svg
							className={iconSizes[size]}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</span>
				)}
			</button>
			{dropdown}
		</div>
	);
};

export default DateTimePicker;
